import React from 'react';
import { useTranslation } from 'react-i18next';

const regionalOffices = [
  {
    name: 'Visakhapatnam Regional Office',
    address: 'DTCP Regional Office, Visakhapatnam, Andhra Pradesh',
    phone: '0891-2700XXX',
    email: 'dtcp-vizag@ap.gov.in',
    jurisdiction: 'Visakhapatnam, Srikakulam, Vizianagaram, East & West Godavari',
  },
  {
    name: 'Guntur Regional Office',
    address: 'DTCP Regional Office, Guntur, Andhra Pradesh',
    phone: '0863-2000XXX',
    email: 'dtcp-guntur@ap.gov.in',
    jurisdiction: 'Guntur, Krishna, Prakasam, Nellore, Palnadu, Bapatla',
  },
  {
    name: 'Kurnool Regional Office',
    address: 'DTCP Regional Office, Kurnool, Andhra Pradesh',
    phone: '08518-2XXXXX',
    email: 'dtcp-kurnool@ap.gov.in',
    jurisdiction: 'Kurnool, Anantapur, Nandyal, Sri Sathya Sai',
  },
  {
    name: 'Tirupati Regional Office',
    address: 'DTCP Regional Office, Tirupati, Andhra Pradesh',
    phone: '0877-2XXXXXX',
    email: 'dtcp-tirupati@ap.gov.in',
    jurisdiction: 'Tirupati, Chittoor, Kadapa, Annamayya',
  },
];

export const ContactPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <a href="/" className="hover:text-amber-700 transition-colors">{t('header.home')}</a>
        <i className="ri-arrow-right-s-line text-stone-400"></i>
        <span className="text-stone-800 font-medium">{t('header.contact')}</span>
      </nav>

      <div className="mb-12">
        <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">{t('contact.title')}</h1>
        <p className="text-lg text-stone-600 max-w-3xl">{t('contact.description')}</p>
      </div>

      {/* Head Office */}
      <div className="mb-12">
        <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
          <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
          {t('contact.headOffice')}
        </h2>
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center text-amber-600 mt-0.5"></i>
                <div>
                  <div className="font-medium text-stone-800">Directorate of Town & Country Planning</div>
                  <div className="text-sm text-stone-600">Government of Andhra Pradesh</div>
                  <div className="text-sm text-stone-600">Mangalagiri, Guntur District</div>
                  <div className="text-sm text-stone-600">Andhra Pradesh - 522503</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-phone-line w-5 h-5 flex items-center justify-center text-amber-600"></i>
                <span className="text-stone-700">0863-2000662</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center text-amber-600"></i>
                <span className="text-stone-700">dtcp-ap@gov.in</span>
              </div>
              <div className="flex items-center space-x-3">
                <i className="ri-global-line w-5 h-5 flex items-center justify-center text-amber-600"></i>
                <span className="text-stone-700">dtcp.ap.gov.in</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <i className="ri-time-line w-5 h-5 flex items-center justify-center text-amber-600"></i>
                <div>
                  <div className="font-medium text-stone-800">{t('contact.supportHours')}</div>
                  <div className="text-sm text-stone-600">Monday - Saturday: 10:00 AM - 5:00 PM</div>
                  <div className="text-sm text-stone-500">Closed on Sundays & Government Holidays</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regional Offices */}
      <div className="mb-12">
        <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
          <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
          {t('contact.regionalOffices')}
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {regionalOffices.map((office, index) => (
            <div key={index} className="bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-amber-300 transition-all">
              <h3 className="font-semibold text-stone-800 mb-2">{office.name}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-start space-x-2 text-stone-600">
                  <i className="ri-map-pin-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                  <span>{office.address}</span>
                </div>
                <div className="flex items-center space-x-2 text-stone-600">
                  <i className="ri-phone-line text-amber-600 flex-shrink-0"></i>
                  <span>{office.phone}</span>
                </div>
                <div className="flex items-center space-x-2 text-stone-600">
                  <i className="ri-mail-line text-amber-600 flex-shrink-0"></i>
                  <span>{office.email}</span>
                </div>
                <div className="flex items-start space-x-2 text-stone-500 mt-2 pt-2 border-t border-stone-100">
                  <i className="ri-map-2-line text-amber-600 mt-0.5 flex-shrink-0"></i>
                  <span><strong>Jurisdiction:</strong> {office.jurisdiction}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Important Portals */}
      <div>
        <h2 className="text-xl font-bold text-stone-800 mb-6 flex items-center">
          <span className="w-1 h-6 bg-amber-600 rounded-full mr-3"></span>
          Online Portals
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { name: 'APDPMS Portal', desc: 'Online building permissions & layout approvals', url: 'https://apdpms.ap.gov.in', icon: 'ri-building-2-line' },
            { name: 'LRS Portal', desc: 'Layout Regularisation Scheme applications', url: 'https://lrs.ap.gov.in', icon: 'ri-map-2-line' },
            { name: 'BPS Portal', desc: 'Building Penalisation Scheme applications', url: 'https://bps.ap.gov.in', icon: 'ri-shield-check-line' },
          ].map((portal, index) => (
            <a key={index} href={portal.url} target="_blank" rel="noopener noreferrer" className="group bg-white border border-stone-200 rounded-lg p-5 hover:shadow-md hover:border-amber-300 transition-all">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
                <i className={`${portal.icon} w-5 h-5 flex items-center justify-center text-amber-600`}></i>
              </div>
              <h3 className="font-semibold text-stone-800 group-hover:text-amber-700 transition-colors">{portal.name}</h3>
              <p className="text-sm text-stone-500 mt-1">{portal.desc}</p>
              <div className="text-xs text-amber-600 mt-2 flex items-center">
                <span>Visit Portal</span>
                <i className="ri-external-link-line ml-1"></i>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};
